import { BackendError, backendFetch, backendJson } from "@/lib/backend";
import { getDocumentDownloadUrl } from "@/lib/api/documents";

const TRANSIENT_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];

function isTransientRequestError(error: unknown): boolean {
  return (
    !(error instanceof BackendError)
    || error.status === 429
    || error.status >= 500
  );
}

async function retryTransientRequest<T>(request: () => Promise<T>): Promise<T> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await request();
    } catch (error) {
      const delay = TRANSIENT_RETRY_DELAYS_MS[attempt];
      if (delay === undefined || !isTransientRequestError(error)) throw error;
      await new Promise((resolve) => window.setTimeout(resolve, delay));
    }
  }
}

export interface SourceTableRef {
  document_id: string;
  sheet_name: string;
  table_type: string;
  table_id: string;
}

export type PrebuildReviewState =
  | "not_started"
  | "running"
  | "questions_required"
  | "ready"
  | "failed";

export interface PrebuildReviewSuggestedAnswer {
  text: string;
  description: string;
  requires_file_upload: boolean;
  recommended: boolean;
}

export interface PrebuildReviewQuestion {
  id: string;
  ordinal: number;
  text: string;
  suggested_answers: PrebuildReviewSuggestedAnswer[];
  source_files: PrebuildReviewSourceFile[];
}

export interface PrebuildReviewSourceFile {
  document_id: string;
  filename: string;
  sheet_name: string | null;
}

export interface PrebuildReviewAnswer {
  question_id: string;
  answer: string;
  attachment_document_ids: string[];
}

export interface PrebuildReview {
  contract_version: "builder_prebuild_review_v5";
  state: PrebuildReviewState;
  batch_id: string | null;
  document_set_fingerprint: string;
  questions: PrebuildReviewQuestion[];
  answers: PrebuildReviewAnswer[];
  job: {
    id: string;
    status: "pending" | "running" | "succeeded" | "failed" | "cancelled";
    attempts: number;
    created_at: string;
    updated_at: string;
  } | null;
  error: string | null;
  retryable: boolean;
}

export interface PrebuildReviewMutation {
  job_id: string;
  state: PrebuildReviewState;
}

export interface BuilderAnalysisSettings {
  financial_year_end_month: number | null;
  analysis_period_start_fy: number | null;
  analysis_period_end_fy: number | null;
  analysis_period_end_is_ytd: boolean;
  forecast_years: number | null;
}

export type BuilderCoveragePeriodStatus = "received" | "missing";

export interface BuilderCoveragePeriod {
  value: string;
  status: BuilderCoveragePeriodStatus;
}

export interface BuilderCoverageCategory {
  source_category: string;
  label: string;
  file_count: number;
  months: BuilderCoveragePeriod[];
  years: BuilderCoveragePeriod[];
}

export interface BuilderCoverageResponse {
  financial_year_end_month: number;
  categories: BuilderCoverageCategory[];
  critical_missing_months: string[];
  critical_missing_pnl_months: string[];
  critical_missing_balance_sheet_months: string[];
  recommend_not_build: boolean;
  has_rfi: boolean;
}

export interface UpdateBuilderAnalysisSettings {
  financial_year_end_month: number;
  analysis_period_start_fy: number;
  analysis_period_end_fy: number;
  analysis_period_end_is_ytd: boolean;
  forecast_years: number | null;
}

export interface BuilderLegalStructureGroup {
  id: string;
  name: string;
  position: number;
}

export type BuilderLegalRelationshipType =
  | "owns"
  | "controls"
  | "subsidiary"
  | "consolidates"
  | "includes_in_group"
  | "unknown";

export interface BuilderLegalEntityDealEvent {
  type: "acquisition" | "disposal";
  effective_month: string | null;
}

export interface BuilderLegalStructureEntity {
  id: string;
  name: string;
  activity_summary: string | null;
  operating_currency: string;
  deal_events: BuilderLegalEntityDealEvent[];
  transaction_perimeter: "inside" | "outside";
  display_group_id: string | null;
  position: number;
  parent_entity_id: string | null;
  relationship_type: BuilderLegalRelationshipType | null;
  active: boolean;
  origin: "source" | "user";
  source_names: string[];
}

export interface BuilderLegalStructure {
  schema_version: 2;
  presentation_currency: string;
  presentation_currency_confirmed: boolean;
  groups: BuilderLegalStructureGroup[];
  entities: BuilderLegalStructureEntity[];
}

export interface BuilderLegalStructureSourceDocument {
  document_id: string;
  filename: string;
  source_type: "excel" | "pdf";
  sheets: string[];
  table_types: string[];
  document_types: string[];
  period_coverage: Array<{
    table_type: string;
    months: Array<{
      value: string;
      status: "received" | "missing";
    }>;
    years: Array<{
      value: string;
      status: "received" | "missing";
    }>;
  }>;
}

export interface BuilderLegalStructureEntityEvidence {
  aliases: string[];
  highlights: string[];
  included_in_consolidation: boolean;
  source_files: BuilderLegalStructureSourceDocument[];
  source_currencies: string[];
  relationships: Array<{
    parent_entity_id: string;
    child_entity_id: string;
    relationship_type: BuilderLegalRelationshipType;
  }>;
}

export interface BuilderLegalStructureResponse {
  source_revision_id: string;
  revision_id: string;
  revision_number: number;
  fingerprint: string;
  structure: BuilderLegalStructure;
  evidence: Record<string, BuilderLegalStructureEntityEvidence>;
  consolidation_sources: BuilderLegalStructureSourceDocument[];
  consolidation_currencies: string[];
  can_edit: boolean;
}

export interface SaveBuilderLegalStructureRequest {
  source_revision_id: string;
  expected_revision_id: string;
  expected_fingerprint: string;
  structure: {
    presentation_currency: string;
    presentation_currency_confirmed: boolean;
    groups: Array<{
      id?: string;
      name: string;
      position: number;
    }>;
    entities: Array<{
      id?: string;
      name: string;
      activity_summary: string | null;
      operating_currency: string;
      deal_events: BuilderLegalEntityDealEvent[];
      transaction_perimeter: "inside" | "outside";
      display_group_id: string | null;
      position: number;
      parent_entity_id: string | null;
      relationship_type: BuilderLegalRelationshipType | null;
      active: boolean;
      attachment_document_ids?: string[];
    }>;
  };
}

export interface BuilderLegalEntityAttachmentMutation {
  job_id: string;
  status: "pending" | "running" | "succeeded" | "failed" | "cancelled";
}

export interface BuilderLegalEntityAttachmentStatus
  extends BuilderLegalEntityAttachmentMutation {
  error: string | null;
}

export type BasisOptionsState =
  | "loaded"
  | "empty"
  | "unavailable"
  | "invalid_artifact"
  | "pending_refresh"
  | "refresh_failed"
  | "needs_review";

export interface BasisDerivationIssue {
  code: string;
  message: string;
  entity_ids: string[];
  table_keys: string[];
}

export interface BasisOptionTableRef {
  document_id: string;
  filename: string;
  source_sheet_name: string;
  table_type: string;
  table_id: string;
}

export interface BasisOptionComponent {
  label: string;
  tables: BasisOptionTableRef[];
  period_usage: {
    months: string[];
    years: string[];
  };
}

export interface BasisOptionStatementPlan {
  mode: "reported" | "consolidated";
  source_components: BasisOptionComponent[];
}

export interface BasisFinancialSummaryPeriod {
  fiscal_year: number;
  actual_months: string[];
  budget_months: string[];
  forecast_months: string[];
  revenue: number | null;
  net_profit: number | null;
  net_assets: number | null;
}

export interface BasisOption {
  id: string;
  recommended: boolean;
  title: string;
  how: string;
  notes?: string | null;
  pnl_plan: BasisOptionStatementPlan | null;
  balance_sheet_plan: BasisOptionStatementPlan | null;
  financial_summary: BasisFinancialSummaryPeriod[];
}

export interface BasisOptionsResponse {
  state: BasisOptionsState;
  builder_context_id: string | null;
  source_revision_id: string | null;
  legal_structure_revision_id: string | null;
  legal_structure_fingerprint: string | null;
  presentation_currency: string | null;
  options: BasisOption[];
  error: string | null;
  basis_status?: "ready" | "agent_required" | "needs_review" | null;
  derivation_kind?:
    | "initial"
    | "carried_forward"
    | "scope_filtered"
    | "scope_restored"
    | "agent_refreshed"
    | null;
  issues?: BasisDerivationIssue[];
}

export interface AskDillyResponse {
  message: string;
  turn: BuilderChatTurn;
  databook_changed: boolean;
}

export interface AskDillyDatabookContext {
  currentSheet: string;
  selectedRange?: string | null;
}

export type BuilderChatStage =
  | "coverage"
  | "questions"
  | "ents"
  | "basis"
  | "book";

export interface BuilderChatTurn {
  id: string;
  source_revision_id: string;
  user_message: string;
  assistant_message: string;
  created_by: string | null;
  builder_stage: BuilderChatStage | null;
  created_at: string;
}

export interface BuilderChatHistoryResponse {
  source_revision_id: string;
  turns: BuilderChatTurn[];
  request: BuilderAgentRequest | null;
}

export type BuilderAgentRequestStatus = "active" | "completed" | "failed";

export type BuilderAgentOperation =
  | "ask_dilly_builder"
  | "ask_dilly_databook";

export interface BuilderAgentRequest {
  request_id: string;
  operation: BuilderAgentOperation;
  channel: "ask_dilly";
  status: BuilderAgentRequestStatus;
  user_message: string;
  builder_stage: BuilderChatStage | null;
  ask_dilly_result: AskDillyResponse | null;
  error: string | null;
}

export type FileReviewResponseState =
  | "loaded"
  | "unavailable"
  | "invalid_artifact";

export type FileReviewItemStatus =
  | "in_use"
  | "set_aside"
  | "available"
  | "unavailable"
  | "partially_used";

export interface FileReviewCounts {
  documents: number;
  source_units: number;
  detected_tables: number;
  selected_tables: number;
  excluded_tables: number;
  unavailable_tables: number;
  available_tables: number;
}

export interface FileReviewDocumentSummary {
  document_id: string;
  filename: string;
  source_type: "excel" | "pdf";
  coverage_status: "analyzed" | "excluded" | "partial" | "failed";
  coverage_reason: string | null;
  status: FileReviewItemStatus;
  source_unit_count: number;
  detected_table_count: number;
  selected_table_count: number;
  excluded_table_count: number;
  unavailable_table_count: number;
  available_table_count: number;
}

export interface FileReviewSummaryResponse {
  state: FileReviewResponseState;
  review_id: string | null;
  source_revision_id: string | null;
  document_set_fingerprint: string | null;
  counts: FileReviewCounts;
  documents: FileReviewDocumentSummary[];
  message: string | null;
}

export interface FileReviewReplacementRef {
  kind: "table" | "candidate";
  table: SourceTableRef | null;
  candidate_id: string | null;
}

export interface FileReviewTableDetail {
  ref: SourceTableRef;
  description: string;
  bounds: Record<string, number>;
  entities: string[];
  reporting_scope: Record<string, unknown> | null;
  status: FileReviewItemStatus;
  editable: boolean;
  reason: string | null;
  confidence: string | null;
  replacement: FileReviewReplacementRef | null;
}

export interface FileReviewPrunedCandidate {
  candidate_id: string;
  sheet_name: string;
  rough_locator: string;
  status: "unavailable";
  editable: false;
  reason: string;
  confidence: number;
  prune_reason: string;
  replacement: FileReviewReplacementRef | null;
}

export interface FileReviewDetectedSection {
  section_type: string;
  source_range: string;
  description: string;
}

export interface FileReviewSourceUnitDetail {
  source_unit_id: string;
  source_type: "excel_sheet" | "pdf_document" | "pdf_section";
  sheet_name: string | null;
  page_range: string | null;
  status: FileReviewItemStatus;
  editable: boolean;
  reason: string | null;
  confidence: number | string | null;
  tables: FileReviewTableDetail[];
  pruned_candidates: FileReviewPrunedCandidate[];
  detected_sections: FileReviewDetectedSection[];
}

export interface FileReviewDocumentDetail {
  document_id: string;
  filename: string;
  source_type: "excel" | "pdf";
  coverage_status: "analyzed" | "excluded" | "partial" | "failed";
  coverage_reason: string | null;
  status: FileReviewItemStatus;
  source_units: FileReviewSourceUnitDetail[];
}

export interface FileReviewDocumentDetailResponse {
  state: FileReviewResponseState;
  review_id: string | null;
  source_revision_id: string | null;
  document_set_fingerprint: string | null;
  document: FileReviewDocumentDetail | null;
  message: string | null;
}

async function reviewMutation(
  projectId: string,
  path: string,
  body?: unknown
): Promise<PrebuildReviewMutation> {
  const { response, data } = await backendJson<PrebuildReviewMutation>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/prebuild-review${path}`,
    {
      method: "POST",
      ...(body === undefined
        ? {}
        : {
            headers: { "content-type": "application/json" },
            body: JSON.stringify(body),
          }),
    }
  );
  if (response.ok && data) return data;
  throw new BackendError("Failed to update Databook Builder review.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export async function getPrebuildReview(projectId: string): Promise<PrebuildReview> {
  const { response, data } = await backendJson<PrebuildReview>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/prebuild-review`,
    { method: "GET" }
  );
  if (response.ok && data) return data;
  throw new BackendError("Failed to load Databook Builder review.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export async function getBuilderAnalysisSettings(
  projectId: string
): Promise<BuilderAnalysisSettings> {
  const { response, data } = await backendJson<BuilderAnalysisSettings>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/prebuild-review/settings`,
    { method: "GET" }
  );
  if (response.ok && data) return data;
  throw new BackendError("Failed to load Databook Builder settings.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export async function getPrebuildReviewCoverage(
  projectId: string
): Promise<BuilderCoverageResponse> {
  const { response, data } = await backendJson<BuilderCoverageResponse>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/prebuild-review/coverage`,
    { method: "GET" }
  );
  if (response.ok && data) return data;
  throw new BackendError("Failed to load source coverage.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export async function updateBuilderAnalysisSettings(
  projectId: string,
  settings: UpdateBuilderAnalysisSettings
): Promise<BuilderAnalysisSettings> {
  const { response, data } = await backendJson<BuilderAnalysisSettings>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/prebuild-review/settings`,
    {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(settings),
    }
  );
  if (response.ok && data) return data;
  throw new BackendError("Failed to save Databook Builder settings.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export async function getPrebuildReviewLegalStructure(
  projectId: string
): Promise<BuilderLegalStructureResponse> {
  const { response, data } = await backendJson<BuilderLegalStructureResponse>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/prebuild-review/legal-structure`,
    { method: "GET" }
  );
  if (response.ok && data) return data;
  throw new BackendError("Failed to load the legal structure.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export async function updatePrebuildReviewLegalStructure(
  projectId: string,
  payload: SaveBuilderLegalStructureRequest,
): Promise<BuilderLegalStructureResponse> {
  const { response, data } = await backendJson<BuilderLegalStructureResponse>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/prebuild-review/legal-structure`,
    {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  if (response.ok && data) return data;
  throw new BackendError("Failed to save the legal structure.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export async function savePrebuildReviewLegalEntityAttachments(
  projectId: string,
  payload: SaveBuilderLegalStructureRequest,
): Promise<BuilderLegalEntityAttachmentMutation> {
  const { response, data } = await backendJson<BuilderLegalEntityAttachmentMutation>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/prebuild-review/legal-structure/attachments`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    },
  );
  if (response.ok && data) return data;
  throw new BackendError("Failed to analyze the legal entity attachments.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export async function getPrebuildReviewLegalEntityAttachmentStatus(
  projectId: string,
  jobId: string,
): Promise<BuilderLegalEntityAttachmentStatus> {
  const { response, data } = await backendJson<BuilderLegalEntityAttachmentStatus>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/prebuild-review/legal-structure/attachments/${encodeURIComponent(jobId)}`,
    { method: "GET" },
  );
  if (response.ok && data) return data;
  throw new BackendError("Failed to load attachment analysis status.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export async function refreshPrebuildReviewBasis(
  projectId: string,
): Promise<void> {
  const response = await backendFetch(
    `/api/projects/${encodeURIComponent(projectId)}/databook/prebuild-review/legal-structure/basis-refresh`,
    { method: "POST" },
  );
  if (response.status === 202) return;
  throw new BackendError("Failed to refresh Basis options.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export async function getPrebuildReviewBasisOptions(
  projectId: string
): Promise<BasisOptionsResponse> {
  const { response, data } = await backendJson<BasisOptionsResponse>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/prebuild-review/basis-options`,
    { method: "GET" }
  );
  if (response.ok && data) return data;
  throw new BackendError("Failed to load basis of preparation options.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export async function downloadBasisComponentSources(
  projectId: string,
  component: BasisOptionComponent,
): Promise<void> {
  if (component.tables.length === 0) {
    throw new Error("This Basis component has no source files.");
  }
  const response = await backendFetch(
    `/api/projects/${encodeURIComponent(projectId)}/documents/download-package`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        label: component.label,
        sources: component.tables.map((table) => ({
          document_id: table.document_id,
          sheet_name: table.source_sheet_name,
        })),
      }),
    },
  );
  if (!response.ok) {
    throw new BackendError("Failed to download Basis component sources.", {
      status: response.status,
      bodyText: await readBodyTextSafe(response),
    });
  }

  const uniqueDocumentIds = new Set(
    component.tables.map((table) => table.document_id),
  );
  const fallbackFilename = uniqueDocumentIds.size === 1
    ? component.tables[0].filename
    : `${component.label} sources.zip`;
  const filename =
    filenameFromContentDisposition(response.headers.get("content-disposition"))
    ?? fallbackFilename;
  const url = URL.createObjectURL(await response.blob());
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function downloadPrebuildQuestionSource(
  projectId: string,
  source: PrebuildReviewSourceFile,
): Promise<void> {
  const suffix = source.filename.toLowerCase();
  if (
    source.sheet_name === null
    || (!suffix.endsWith(".xlsx") && !suffix.endsWith(".xlsm"))
  ) {
    const { url } = await getDocumentDownloadUrl(projectId, source.document_id);
    const link = document.createElement("a");
    link.href = url;
    link.download = source.filename;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
    return;
  }

  const response = await backendFetch(
    `/api/projects/${encodeURIComponent(projectId)}/documents/download-package`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        label: "Question source",
        sources: [
          {
            document_id: source.document_id,
            sheet_name: source.sheet_name,
          },
        ],
      }),
    },
  );
  if (!response.ok) {
    throw new BackendError("Failed to download question source.", {
      status: response.status,
      bodyText: await readBodyTextSafe(response),
    });
  }

  const filename =
    filenameFromContentDisposition(response.headers.get("content-disposition"))
    ?? source.filename;
  const url = URL.createObjectURL(await response.blob());
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function askBuilderSourceAnalysis(
  projectId: string,
  question: string,
  stage: BuilderChatStage,
  databookContext?: AskDillyDatabookContext,
): Promise<BuilderAgentRequest> {
  const { response, data } = await backendJson<BuilderAgentRequest>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/prebuild-review/ask-dilly`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        question,
        stage,
        ...(databookContext
          ? {
              current_sheet: databookContext.currentSheet,
              selected_range: databookContext.selectedRange ?? null,
            }
          : {}),
      }),
    },
  );
  if (response.ok && data) return data;
  throw new BackendError("Ask Dilli could not process the question.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export async function getBuilderAgentRequest(
  projectId: string,
  requestId: string,
): Promise<BuilderAgentRequest> {
  const { response, data } = await backendJson<BuilderAgentRequest>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/prebuild-review/agent-requests/${encodeURIComponent(requestId)}`,
    { method: "GET" },
  );
  if (response.ok && data) return data;
  throw new BackendError("Failed to check the Builder agent request.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export async function getPrebuildReviewAskDillyHistory(
  projectId: string,
): Promise<BuilderChatHistoryResponse> {
  const { response, data } = await backendJson<BuilderChatHistoryResponse>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/prebuild-review/ask-dilly/history`,
    { method: "GET" },
  );
  if (response.ok && data) return data;
  throw new BackendError("Failed to load Ask Dilli history.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export async function getPrebuildFileReview(
  projectId: string
): Promise<FileReviewSummaryResponse> {
  const { response, data } = await backendJson<FileReviewSummaryResponse>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/prebuild-review/file-review`,
    { method: "GET" }
  );
  if (response.ok && data) return data;
  throw new BackendError("Failed to load File review.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export async function getPrebuildFileReviewDocument(
  projectId: string,
  documentId: string
): Promise<FileReviewDocumentDetailResponse> {
  const { response, data } = await backendJson<FileReviewDocumentDetailResponse>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/prebuild-review/file-review/documents/${encodeURIComponent(documentId)}`,
    { method: "GET" }
  );
  if (response.ok && data) return data;
  throw new BackendError("Failed to load File review document.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export function startPrebuildReview(
  projectId: string,
  dealContext: string,
  settings: UpdateBuilderAnalysisSettings
): Promise<PrebuildReviewMutation> {
  return retryTransientRequest(() =>
    reviewMutation(projectId, "/start", {
      deal_context: dealContext,
      ...settings,
    })
  );
}

export function retryPrebuildReview(projectId: string): Promise<PrebuildReviewMutation> {
  return reviewMutation(projectId, "/retry");
}

export function submitPrebuildReviewAnswers(
  projectId: string,
  batchId: string,
  answers: PrebuildReviewAnswer[]
): Promise<PrebuildReviewMutation> {
  return reviewMutation(projectId, "/answers", {
    batch_id: batchId,
    answers,
  });
}

export interface DatabookOutcome {
  job_id: string | null;
  version_id: string | null;
  error_type: "model_unavailable" | "other" | null;
  retry: { allowed: boolean; available_at: string | null; retry_job_id: string | null };
}

export interface DatabookJobStatus extends DatabookOutcome {
  state: "queued" | "finalizing" | "running" | "ready" | "failed";
  progress: {
    completed: number;
    total: number;
  } | null;
  builder_context_id: string | null;
  basis_id: string | null;
}

async function readBodyTextSafe(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return "";
  }
}

export async function runDatabook(
  projectId: string,
  payload: {
    financial_year_end_month: number;
    industry?: string | null;
    source_revision_id: string;
    legal_structure_revision_id: string;
    legal_structure_fingerprint: string;
    basis_id: string;
  }
): Promise<void> {
  const response = await backendFetch(
    `/api/projects/${encodeURIComponent(projectId)}/databook/run_databook`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  if (response.status === 204) return;

  const bodyText = await readBodyTextSafe(response);
  throw new BackendError("Failed to start databook generation.", {
    status: response.status,
    bodyText,
  });
}

export async function getDatabookJobStatus(
  projectId: string
): Promise<DatabookJobStatus> {
  return retryTransientRequest(async () => {
    const { response, data } = await backendJson<DatabookJobStatus>(
      `/api/projects/${encodeURIComponent(projectId)}/databook/job_status`,
      { method: "GET" }
    );

    if (response.ok && data !== null) return data;

    const bodyText = await readBodyTextSafe(response);
    throw new BackendError("Failed to get databook job status.", {
      status: response.status,
      bodyText,
    });
  });
}

export interface DatabookSheetListItem {
  name: string;
  selectable: boolean;
  size_bytes?: number;
}

export interface DatabookSheetList {
  version_id: string;
  sheets: string[];
  items: DatabookSheetListItem[];
  alert_sheets: string[];
}

export async function getDatabookSheets(
  projectId: string,
  versionId?: string,
): Promise<DatabookSheetList> {
  const { response, data } = await backendJson<DatabookSheetList>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/sheets${versionId ? `?version_id=${encodeURIComponent(versionId)}` : ""}`,
    { method: "GET" },
  );

  if (response.ok && data !== null) return data;

  throw new BackendError("Failed to load databook sheets.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export interface DatabookSheetDeletionImpact {
  base_version_id: string;
  base_generation: string;
  selected_sheet_names: string[];
  dependent_sheet_names: string[];
}

export interface DatabookSheetDeletionResult {
  version_id: string;
  gcs_generation: string | null;
  selected_sheet_names: string[];
  dependent_sheet_names: string[];
}

export async function analyzeDatabookSheetDeletion(
  projectId: string,
  sheetNames: string[],
): Promise<DatabookSheetDeletionImpact> {
  const { response, data } = await backendJson<DatabookSheetDeletionImpact>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/sheet-deletion/impact`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ sheet_names: sheetNames }),
    },
  );

  if (response.ok && data !== null) return data;

  throw new BackendError("Failed to analyze worksheet deletion.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export async function applyDatabookSheetDeletion(
  projectId: string,
  impact: DatabookSheetDeletionImpact,
): Promise<DatabookSheetDeletionResult> {
  const { response, data } = await backendJson<DatabookSheetDeletionResult>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/sheet-deletion/apply`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        sheet_names: impact.selected_sheet_names,
        base_version_id: impact.base_version_id,
        base_generation: impact.base_generation,
      }),
    },
  );

  if (response.ok && data !== null) return data;

  throw new BackendError("Failed to delete worksheets.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export interface DatabookPreviewFont {
  name: string | null;
  size: number | null;
  color: string | null;
  bold: boolean;
  italic: boolean;
  underline: boolean;
}

export interface DatabookPreviewFill {
  color: string | null;
}

export interface DatabookPreviewBorderSide {
  style: string | null;
  color: string | null;
}

export interface DatabookPreviewStyle {
  id: number;
  font: DatabookPreviewFont;
  fill: DatabookPreviewFill;
  border: {
    top: DatabookPreviewBorderSide;
    right: DatabookPreviewBorderSide;
    bottom: DatabookPreviewBorderSide;
    left: DatabookPreviewBorderSide;
  };
  alignment: {
    horizontal: string | null;
    vertical: string | null;
    wrap_text: boolean;
    indent: number;
  };
  number_format: string;
}

export interface DatabookPreviewCell {
  row: number;
  column: number;
  kind: "blank" | "string" | "number" | "boolean" | "date" | "error";
  value: string | number | boolean | null;
  display_text: string;
  style_id: number;
}

export interface DatabookPreviewOutlineGroup {
  id: string;
  level: number;
  start_source_index: number;
  end_source_index: number;
  summary_source_index: number;
}

export interface DatabookQaQuestion {
  databook_tab: string;
  metric: string;
  period: string;
  value: number | null;
  variance: number | null;
  question: string;
  priority: "Critical" | "High" | "Medium" | "Low";
}

export interface DatabookVarianceFinding {
  cell_address: string;
  text: string;
  check_index: number;
  sheet_name: string;
}

export interface DatabookInsights {
  version_id: string;
  variances: DatabookVarianceFinding[];
  questions: DatabookQaQuestion[];
}

export async function getDatabookInsights(
  projectId: string,
  versionId: string,
  signal?: AbortSignal,
): Promise<DatabookInsights> {
  const params = new URLSearchParams({ version_id: versionId });
  const { response, data } = await backendJson<DatabookInsights>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/insights?${params}`,
    { method: "GET", signal },
  );
  if (response.ok && data !== null) return data;
  throw new BackendError("Failed to load databook insights.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export interface DatabookSheetPreview {
  name: string;
  outline_level: number;
  max_outline_level: number;
  max_column_outline_level: number;
  row_count: number;
  row_offset?: number;
  returned_row_count?: number;
  column_count: number;
  default_row_height: number;
  default_column_width: number;
  frozen_rows: number;
  frozen_columns: number;
  show_grid_lines: boolean;
  rows: Array<{
    index: number;
    source_index: number;
    height: number;
    outline_level: number;
  }>;
  columns: Array<{ index: number; source_index: number; width: number }>;
  cells: DatabookPreviewCell[];
  styles: DatabookPreviewStyle[];
  merges: Array<{
    start_row: number;
    start_column: number;
    end_row: number;
    end_column: number;
  }>;
  outline_groups: DatabookPreviewOutlineGroup[];
  expanded_outline_groups: string[];
  column_outline_groups: DatabookPreviewOutlineGroup[];
  expanded_column_outline_groups: string[];
}

export async function getDatabookSheet(
  projectId: string,
  sheetName: string,
  rowOffset = 0,
  expandedOutlineGroups: readonly string[] = [],
  expandedColumnOutlineGroups: readonly string[] = [],
  versionId?: string,
): Promise<DatabookSheetPreview> {
  const params = new URLSearchParams();
  if (versionId) params.set("version_id", versionId);
  if (rowOffset > 0) params.set("row_offset", String(rowOffset));
  [...expandedOutlineGroups].sort().forEach((groupId) => {
    params.append("expanded_group", groupId);
  });
  [...expandedColumnOutlineGroups].sort().forEach((groupId) => {
    params.append("expanded_column_group", groupId);
  });
  const queryString = params.toString();
  const query = queryString ? `?${queryString}` : "";
  const { response, data } = await backendJson<DatabookSheetPreview>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/sheets/${encodeURIComponent(sheetName)}${query}`,
    { method: "GET" },
  );

  if (response.ok && data !== null) return data;

  throw new BackendError("Failed to load the databook sheet.", {
    status: response.status,
    bodyText: await readBodyTextSafe(response),
  });
}

export function filenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const mStar = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (mStar?.[1]) {
    try {
      return decodeURIComponent(mStar[1]);
    } catch {
      return mStar[1];
    }
  }
  const m =
    /filename\s*=\s*"([^"]+)"/i.exec(header) ??
    /filename\s*=\s*([^;]+)/i.exec(header);
  if (!m?.[1]) return null;
  return m[1].trim();
}

export async function downloadDatabook(projectId: string): Promise<void> {
  const response = await backendFetch(
    `/api/projects/${encodeURIComponent(projectId)}/databook/download`,
    { method: "GET" }
  );

  if (!response.ok) {
    const bodyText = await readBodyTextSafe(response);
    throw new BackendError("Failed to download databook.", {
      status: response.status,
      bodyText,
    });
  }

  const blob = await response.blob();
  const contentDisposition = response.headers.get("content-disposition");
  const filename =
    filenameFromContentDisposition(contentDisposition) ??
    `databook-${projectId}.xlsx`;

  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}


export async function retryDatabookJob(projectId: string, jobId: string): Promise<void> {
  const response = await backendFetch(`/api/projects/${encodeURIComponent(projectId)}/databook/jobs/${encodeURIComponent(jobId)}/retry`, { method: "POST" });
  if (!response.ok) throw new BackendError("This retry is unavailable.", { status: response.status });
}

export async function getDatabookVersionOutcome(projectId: string, versionId: string): Promise<DatabookOutcome> {
  const { response, data } = await backendJson<DatabookOutcome>(`/api/projects/${encodeURIComponent(projectId)}/databook/versions/${encodeURIComponent(versionId)}/outcome`, { method: "GET" });
  if (response.ok && data) return data;
  throw new BackendError("Could not load databook outcome.", { status: response.status });
}

export interface DatabookVersion {
  id: string;
  version_number: number;
  created_at: string | null;
}

export async function listDatabookVersions(
  projectId: string
): Promise<DatabookVersion[]> {
  const { response, data } = await backendJson<{ versions: DatabookVersion[] }>(
    `/api/projects/${encodeURIComponent(projectId)}/databook/versions`,
    { method: "GET" }
  );

  if (response.ok && data) return data.versions;

  const bodyText = await readBodyTextSafe(response);
  throw new BackendError("Failed to list databook versions.", {
    status: response.status,
    bodyText,
  });
}

export interface UploadedDatabookVersion {
  id: string;
  project_id: string;
  source: string;
  original_filename: string | null;
  gcs_generation: string | null;
  created_at: string;
}

export async function uploadDatabookVersion(
  projectId: string,
  file: File
): Promise<UploadedDatabookVersion> {
  const form = new FormData();
  form.append("file", file);

  const response = await backendFetch(
    `/api/projects/${encodeURIComponent(projectId)}/databook/upload`,
    {
      method: "POST",
      body: form,
    }
  );

  if (response.ok) {
    const contentType = response.headers.get("content-type") ?? "";
    if (contentType.includes("application/json")) {
      return (await response.json()) as UploadedDatabookVersion;
    }
  }

  const bodyText = await readBodyTextSafe(response);
  throw new BackendError("Failed to upload databook.", {
    status: response.status,
    bodyText,
  });
}

export async function downloadDatabookVersion(
  projectId: string,
  versionId: string
): Promise<void> {
  const response = await backendFetch(
    `/api/projects/${encodeURIComponent(projectId)}/databook/versions/${encodeURIComponent(versionId)}/download`,
    { method: "GET" }
  );

  if (!response.ok) {
    const bodyText = await readBodyTextSafe(response);
    throw new BackendError("Failed to download databook version.", {
      status: response.status,
      bodyText,
    });
  }

  const blob = await response.blob();
  const filename =
    filenameFromContentDisposition(response.headers.get("content-disposition")) ??
    `databook-${projectId}.xlsx`;

  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function downloadDatabookQnaExcel(
  projectId: string,
  versionId: string
): Promise<void> {
  const response = await backendFetch(
    `/api/projects/${encodeURIComponent(projectId)}/databook/qna/excel?version_id=${encodeURIComponent(versionId)}`,
    { method: "GET" }
  );

  if (!response.ok) {
    const bodyText = await readBodyTextSafe(response);
    throw new BackendError("Failed to download Q&A.", {
      status: response.status,
      bodyText,
    });
  }

  const blob = await response.blob();
  const filename =
    filenameFromContentDisposition(response.headers.get("content-disposition")) ??
    `QA-${projectId}.xlsx`;

  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.rel = "noopener";
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
